export type MockStudyScenario = {
  studyGoal: string;
  researchPurpose: string;
  keyAspects: string[];
  background: string;
  openingMessage: string;
  closingMessage: string;
  interviewQuestions: string[];
  maxFollowups: number;
  modeOverride: string;
};

export const FULL_FLOW_SCENARIO: MockStudyScenario = {
  studyGoal: 'Understand how people use AI as companionship',
  researchPurpose:
    'Explore the phenomenon openly with no predetermined outcome (Exploratory). Investigate how people experience and make sense of AI companionship across diverse contexts without constraining findings to a specific disciplinary framework.',
  keyAspects: [
    'Nature and frequency of AI interactions and how they fit into daily life',
    'Emotional needs being met and perceived benefits of AI companionship',
    'Impact on human relationships and boundaries between AI and human connection',
  ],
  background:
    'This study is run by Columbia Business School. Participants are adults who have interacted with AI for companionship and social connection. This includes those using general LLMs like ChatGPT and Claude, or specific AI companion apps like Replika and Character.ai.',
  openingMessage: `Hello, and thank you for taking the time to speak with me today. I'm an AI interviewer, and I'm part of a research study to understand how people use AI companionship.

In this interview, I'll ask you a few questions about your experiences. There are no right or wrong answers — I'm simply here to listen and learn from your perspective.

Some questions may feel personal, but please feel free to share only what you are comfortable with. To avoid interrupting your response, I may sometimes wait a little longer before speaking, so please don’t worry if there is a brief pause. This conversation should take about 20 minutes. 

Are you ready to get started?`,
  closingMessage: `Thank you so much for sharing your thoughts and experiences with me today. Your responses are incredibly valuable to this research.

The conversation is now saved and the research team will be in touch if there's any follow-up.

Thank you again for your time.`,
  interviewQuestions: [
    'Can you walk me through a typical day or week and tell me where and when you interact with your AI companion app?',
    'What do you usually turn to your AI companion for, and what does it give you in those moments?',
    'How would you describe your relationship with your AI companion? In what ways, if any, does it feel like it understands you as a person?',
    'Has using your AI companion impacted your relationships with friends, family, or romantic partner? Has it changed how you interact with other people, if at all?',
    'What impact has using an AI companion had on your life? What would you say have been the biggest benefits and the biggest drawbacks?',
    'If you could redesign your AI companion to better fit your needs, what would you change?'
  ],
  maxFollowups: 2,
  modeOverride: '',
};

export const QUICK_ENDING_SCENARIO: MockStudyScenario = {
  // Keep research context identical to production-like flow; only shorten the
  // conversational flow for faster ending-state tests.
  studyGoal: FULL_FLOW_SCENARIO.studyGoal,
  researchPurpose: FULL_FLOW_SCENARIO.researchPurpose,
  keyAspects: FULL_FLOW_SCENARIO.keyAspects,
  background: FULL_FLOW_SCENARIO.background,
  openingMessage: `Hi, thanks for joining this quick test interview.

I will ask one short question, then we will end the interview.

Are you ready to begin?`,
  closingMessage: 'Thanks for your time. This quick test interview is complete.',
  interviewQuestions: [
    'In one or two sentences, what is one thing you find useful about AI companionship?',
  ],
  maxFollowups: 2,
  modeOverride: `

# Test Mode Override (highest priority)

- Keep this interview intentionally short.
- Ask exactly one main question from the Interview Outline.
- Ask up to 2 concise follow-up questions only if needed for clarification.
- After receiving one substantive answer, ask whether the respondent wants to add anything else.
- If they do not add anything substantial, call the \`end_interview\` tool alone (no closing speech in that turn), then deliver the closing message in the following turn.
`,
};

const QUICK_ENDING_TEST_MODE = process.env.NEXT_PUBLIC_QUICK_ENDING_TEST === '1';

export function getActiveMockStudyScenario(): MockStudyScenario {
  return QUICK_ENDING_TEST_MODE ? QUICK_ENDING_SCENARIO : FULL_FLOW_SCENARIO;
}
